import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigation, Truck, Radio, MapPin, Loader2, Power, Play, Pause, History, X, ExternalLink } from "lucide-react";

const US_CENTER = [39.5, -98.35];
const HOURS = [1, 6, 24, 168];
const HOUR_LABEL = { 1: "1h", 6: "6h", 24: "24h", 168: "7d" };
const SPEEDS = [1, 2, 4];

function truckIcon(active) {
  return L.divIcon({
    className: "",
    html: `<div style="width:34px;height:34px;border-radius:50%;background:${active ? "#ffb400" : "#94a3b8"};
      border:3px solid #0b2a6b;display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(15,44,99,.4);">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="#0b2a6b" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/>
        <circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
    </div>`,
    iconSize: [34, 34], iconAnchor: [17, 17],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 9);
    else if (points.length > 1) map.fitBounds(points, { padding: [50, 50] });
  }, [points, map]);
  return null;
}

export default function Tracking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [shareErr, setShareErr] = useState("");
  const watchRef = useRef(null);
  const isDriver = user.role === "driver";

  // playback state
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [hours, setHours] = useState(24);
  const [playIdx, setPlayIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const playRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/tracking/drivers");
    setDrivers(r.data.drivers || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // load history when a driver is selected or timeframe changes
  useEffect(() => {
    if (!selected) { setHistory([]); return; }
    api.get(`/tracking/history/${selected}`, { params: { hours } }).then((r) => {
      const p = r.data.pings || [];
      setHistory(p);
      setPlayIdx(Math.max(0, p.length - 1));
      setPlaying(false);
    }).catch(() => setHistory([]));
  }, [selected, hours]);

  // playback ticker
  useEffect(() => {
    if (!playing || history.length < 2) return;
    playRef.current = setInterval(() => {
      setPlayIdx((i) => { if (i >= history.length - 1) { setPlaying(false); return i; } return i + 1; });
    }, 700 / speed);
    return () => clearInterval(playRef.current);
  }, [playing, speed, history]);

  const togglePlay = () => {
    if (history.length < 2) return;
    if (playIdx >= history.length - 1) setPlayIdx(0);
    setPlaying((p) => !p);
  };

  const toggleShare = () => {
    if (sharing) {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null; setSharing(false); return;
    }
    if (!navigator.geolocation) { setShareErr("Geolocation not supported on this device."); return; }
    setShareErr("");
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await api.post("/tracking/ping", { lat: pos.coords.latitude, lng: pos.coords.longitude, heading: pos.coords.heading, speed: pos.coords.speed });
          load();
        } catch (e) { /* ignore */ }
      },
      (err) => { setShareErr(err.message || "Location permission denied."); setSharing(false); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    setSharing(true);
  };
  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); }, []);

  const livePoints = useMemo(() => drivers.filter((d) => typeof d.lat === "number").map((d) => [d.lat, d.lng]), [drivers]);
  const trail = useMemo(() => history.map((p) => [p.lat, p.lng]), [history]);
  const fitPoints = selected && trail.length ? trail : livePoints;
  const cur = history[playIdx];
  const selDriver = drivers.find((d) => d.user_id === selected);

  const fresh = (ts) => ts && (Date.now() - new Date(ts).getTime()) < 5 * 60 * 1000;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Live operations</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Fleet tracking</h1>
          <p className="text-slate-500 mt-2">Real-time GPS positions{isDriver ? "" : " and route history playback"} across active loads.</p>
        </div>
        {isDriver && (
          <button onClick={toggleShare} className={`${sharing ? "btn-secondary" : "btn-primary"} text-base px-5 py-3`} data-testid="toggle-share-location">
            {sharing ? <><Radio size={16} className="animate-pulse" /> Sharing live · Stop</> : <><Power size={16} /> Share my location</>}
          </button>
        )}
      </div>
      {shareErr && <div className="card-base p-3 border-red-300 bg-red-50 text-sm text-red-700" data-testid="share-error">{shareErr}</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-base overflow-hidden" data-testid="tracking-map">
          <div style={{ height: 460 }}>
            <MapContainer center={US_CENTER} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds points={fitPoints} />
              {/* Live driver markers (hidden when in playback to reduce clutter) */}
              {!selected && drivers.filter((d) => typeof d.lat === "number").map((d) => (
                <Marker key={d.user_id} position={[d.lat, d.lng]} icon={truckIcon(fresh(d.updated_at))}
                  eventHandlers={{ click: () => setSelected(d.user_id) }}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-navy">{d.name}</div>
                      {d.route && <div className="text-slate-600">{d.route}</div>}
                      <button className="text-gold font-semibold mt-1" onClick={() => setSelected(d.user_id)}>View route history →</button>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Playback trail */}
              {selected && trail.length > 1 && (
                <>
                  <Polyline positions={trail} pathOptions={{ color: "#ffb400", weight: 4, opacity: 0.85 }} />
                  <CircleMarker center={trail[0]} radius={6} pathOptions={{ color: "#0b2a6b", fillColor: "#fff", fillOpacity: 1, weight: 2 }} />
                  {cur && <CircleMarker center={[cur.lat, cur.lng]} radius={10} pathOptions={{ color: "#0b2a6b", fillColor: "#ffb400", fillOpacity: 1, weight: 3 }} />}
                </>
              )}
            </MapContainer>
          </div>

          {/* Playback controls */}
          {selected && (
            <div className="border-t border-slate-100 p-4 space-y-3" data-testid="playback-controls">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm font-bold text-navy">
                  <History size={16} className="text-gold" /> {selDriver?.name || "Route"} · {history.length} pts
                </div>
                <div className="flex items-center gap-1.5">
                  {HOURS.map((h) => (
                    <button key={h} onClick={() => setHours(h)} className={`text-[11px] font-bold px-2 py-1 rounded-full ${hours === h ? "bg-navy text-white" : "bg-slate-100 text-slate-500 hover:text-navy"}`} data-testid={`hours-${h}`}>{HOUR_LABEL[h]}</button>
                  ))}
                  <button onClick={() => { setSelected(null); setPlaying(false); }} className="ml-1 text-slate-400 hover:text-navy p-1" data-testid="close-playback"><X size={16} /></button>
                </div>
              </div>
              {history.length < 2 ? (
                <div className="text-sm text-slate-400" data-testid="no-history">No route history in this window.</div>
              ) : (
                <div className="flex items-center gap-3">
                  <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-gold text-navy flex items-center justify-center flex-shrink-0" data-testid="play-pause">
                    {playing ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  <input type="range" min="0" max={history.length - 1} value={playIdx}
                    onChange={(e) => { setPlaying(false); setPlayIdx(Number(e.target.value)); }}
                    className="flex-1 accent-gold" data-testid="playback-slider" />
                  <div className="text-xs text-slate-500 font-mono whitespace-nowrap min-w-[64px] text-right" data-testid="playback-time">
                    {cur ? new Date(cur.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </div>
                  <div className="flex items-center gap-1">
                    {SPEEDS.map((s) => (
                      <button key={s} onClick={() => setSpeed(s)} className={`text-[11px] font-bold px-1.5 py-1 rounded ${speed === s ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`speed-${s}`}>{s}x</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Driver list */}
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Drivers ({drivers.length})</div>
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : drivers.length === 0 ? (
            <div className="card-base p-8 text-center" data-testid="no-tracking">
              <MapPin size={26} className="text-slate-300 mx-auto mb-2" />
              <div className="text-sm text-slate-500">{isDriver ? "Tap “Share my location” to appear on the map." : "No drivers are sharing location yet."}</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="tracking-list">
              {drivers.map((d) => (
                <div key={d.user_id} onClick={() => setSelected(d.user_id)}
                  className={`card-base p-4 cursor-pointer transition-colors ${selected === d.user_id ? "border-gold ring-1 ring-gold" : "hover:border-gold"}`}
                  data-testid={`tracking-driver-${d.user_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${fresh(d.updated_at) ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <span className="font-semibold text-navy text-sm">{d.name}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 capitalize flex items-center gap-1"><Truck size={11} /> {d.equipment_type || "—"}</span>
                  </div>
                  {d.route && <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1"><Navigation size={11} className="text-gold" /> {d.route}</div>}
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[11px] text-slate-400">{fresh(d.updated_at) ? "Live" : "Last seen"} · {d.updated_at ? new Date(d.updated_at).toLocaleTimeString() : "—"}</div>
                    {d.load_id && (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/app/loads/${d.load_id}`); }} className="text-[11px] font-bold text-gold flex items-center gap-1" data-testid={`view-load-${d.user_id}`}>
                        Load <ExternalLink size={10} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
